/**
 * The set of v2 scalar metric keys the backend exposes. camelCase end-to-end —
 * these strings appear in querystrings AND as response object keys.
 */
export type ScalarMetricKey =
  | 'aiMessages'
  | 'aiAcceptanceRate'
  | 'aiCoverage'
  | 'aiTicketCoverage'
  | 'aiAutomation'
  | 'aiAccuracy'
  | 'aiEdits'
  | 'avgAiResponsesPerTicket'
  | 'aiSurfacedAccuracy'
  | 'aiGeneratedCoverage'
  | 'aiSurfacedCoverage';

/**
 * The metrics shown on the Message Metrics tab's Overview section. Order
 * matters (grid layout). `avgAiResponsesPerTicket` is a member of the
 * `ScalarMetricKey` union but lives on the Ticket Metrics tab; it is fetched
 * by `TicketMetricsSection`, not by `OverviewSection`.
 */
export const SCALAR_METRIC_KEYS: ScalarMetricKey[] = ['aiMessages', 'aiAcceptanceRate', 'aiCoverage', 'aiAutomation', 'aiAccuracy', 'aiEdits'];

/**
 * Scalar keys fetched on the Ticket Metrics tab. Three real metrics (the
 * `Productivity Gain` card is a placeholder, no backend call yet).
 *   - aiTicketCoverage powers "Tickets Handled by AI" (distinct conversations
 *     handled by AI / distinct CS conversations, rendered as count/total)
 *   - avgAiResponsesPerTicket powers "Avg AI responses /ticket"
 *   - aiAutomation powers "Automation Rate" (rendered as percent)
 */
export const TICKET_METRIC_KEYS: ScalarMetricKey[] = ['aiTicketCoverage', 'avgAiResponsesPerTicket', 'aiAutomation'];

/**
 * One snapshot — count, total, and the derived percent.
 */
export interface MetricSnapshot {
  count: number;
  total: number;
  percent: number | null;
}

/**
 * Two snapshots side-by-side plus both trend forms:
 *  - `countChangePct` — relative growth of the count vs prev period (300 → 360 = +20%)
 *  - `percentPointChange` — literal subtraction of two percents (30% → 36% = +6 pp)
 *
 * The card decides which trend to surface based on what it's displaying.
 */
export interface ScalarMetricResult {
  current: MetricSnapshot;
  previous: MetricSnapshot;
  trend: {
    countChangePct: number | null;
    percentPointChange: number | null;
  };
}

export type ScalarMetricsBatch = Partial<Record<ScalarMetricKey, ScalarMetricResult>>;

export interface AnalyticsV2QueryParams {
  fromDate: string; // YYYY-MM-DD
  toDate: string;   // YYYY-MM-DD
  /** Account to read. Always explicit — the Control Panel has no own account. */
  accountId: number;
  /**
   * Brand to scope to, or null/undefined for the whole account.
   *
   * A STRING because `business_units.id` is a Postgres BIGINT that machine serialises as
   * a string, and because it goes straight onto the query string. Never coerced to a
   * number on the way out: kim-analytics validates it with a digit pattern.
   *
   * Unlike the product frontend there is no operator brand scope to follow here — the
   * Control Panel inspects OTHER accounts, so the brand belongs to the account being
   * viewed and is picked per view.
   */
  buId?: string | null;
  /** Picked values per ticket dimension (e.g. channel, category); `null` picks the tickets with no value. */
  filters?: MetricFilters;
}

export type MetricFilters = Partial<Record<string, (string | null)[]>>;

/** One group of a /metrics/breakdown response: its dimension values and each metric over its tickets. */
export interface MetricBreakdownRow<D extends string = string, K extends string = string> {
  dims: Partial<Record<D, string | null>>;
  metrics: Partial<Record<K, MetricSnapshot>>;
}

/**
 * Series metric keys mirror scalar keys — a card and its trend chart share one
 * identifier end-to-end. Only metrics with a backend series implementation
 * appear here; cards without series simply don't render a chart.
 */
export type SeriesMetricKey = 'aiMessages' | 'aiAcceptanceRate' | 'aiCoverage' | 'aiAutomation' | 'aiAccuracy' | 'aiEdits';

export const SERIES_METRIC_KEYS: SeriesMetricKey[] = ['aiMessages', 'aiAcceptanceRate', 'aiCoverage', 'aiAutomation', 'aiAccuracy', 'aiEdits'];

export type Granularity = 'day' | 'week' | 'month';

export interface SeriesBucket {
  date: string;        // YYYY-MM-DD — start of bucket
  count: number;
  total: number;
  percent: number | null;
}

export interface SeriesMetricResult {
  granularity: Granularity;
  buckets: SeriesBucket[];
}

export type SeriesMetricsBatch = Partial<Record<SeriesMetricKey, SeriesMetricResult>>;

export interface AnalyticsV2SeriesQueryParams extends AnalyticsV2QueryParams {
  granularity: Granularity;
}


// ─── Categories ───────────────────────────────────────────────────────────────
// One row per category in the rollup for the Ticket Metrics tab's
// "Metrics by Category" table. Shape mirrors the backend response from
// `GET /api/analytics-v2/categories`.

export type TicketCategoryBucket = 'CS' | 'Non CS' | 'B2B' | 'Spam';

export interface CategoryTableRow {
  category: string;
  tickets: number;
  avgAiResponsesPerTicket: number | null;
  withAi: { count: number; percent: number | null };
  automated: { count: number; percent: number | null };
  ticketCategory: TicketCategoryBucket | null;
}

export interface AnalyticsV2CategoriesQueryParams extends AnalyticsV2QueryParams {
  /** Optional bucket filter. When unset, all categories return. */
  ticketCategory?: TicketCategoryBucket;
}


// ─── Coverage Funnel ────────────────────────────────────────────────────────────
// The Coverage Funnel section on the super-admin Internal Analytics page.
// Mirrors the shadow report's Coverage Funnel sheet: seven stages descending
// from CS Tickets, each with its share of csTickets. Shape mirrors the backend
// response from `GET /api/analytics/funnel`.

/** One funnel stage: raw count and its share of csTickets (already *100, 2dp). */
export interface CoverageFunnelStage {
  count: number;
  pct: number | null;
}

export interface CoverageFunnelResult {
  csTickets: number;
  aiGenerated: CoverageFunnelStage; // AI Generated Response
  surfaced: CoverageFunnelStage; // Surfaced (confidence >= 0.5)
  nonSurfaced: CoverageFunnelStage; // Non-Surfaced
  hitlPending: CoverageFunnelStage; // HITL Pending
  agentAdded: CoverageFunnelStage; // Agent Added to Composer
  agentSent: CoverageFunnelStage; // Agent Sent to Customer
  hitl: { approved: number; rejected: number; pending: number };
}


// ─── AI Cost Ledger ─────────────────────────────────────────────────────────────
// `GET /api/analytics-v2/cost/*` in kim-analytics (routes/analytics-v2/cost), relayed by
// machine `/admin/analytics/cost/*`. Shapes are the response `data`; money is USD.

export type CostGranularity = 'day' | 'month' | 'year';
/**
 * `GET /cost/export?type=`: CSV of tickets, usage_daily rows, the weekly brand report
 * (honours `level`), or every raw call / response (both max 31 days).
 */
export type CostExportType = 'tickets' | 'daily' | 'weekly' | 'calls' | 'responses';
/** `area` groups by feature area: rows / points carry the area `key` and its `label`. */
export type CostSeriesGroupBy = 'none' | 'area' | 'feature' | 'service' | 'model' | 'brand';
export type CostBreakdownGroupBy = 'area' | 'feature' | 'service' | 'model' | 'brand' | 'provider';

/**
 * Optional ticket filters every cost endpoint (and the CSV export) accepts. Any of them set
 * means only costs tied to a matching ticket; responses then say `ticketFilterApplied: true`.
 */
export interface CostTicketFilter {
  channel?: string | null;
  category?: string | null;
  intent?: string | null;
}

/** Range + brand + ticket filters for the cost endpoints. */
export type CostQueryParams = AnalyticsV2QueryParams & CostTicketFilter;

/** Present (true) when a ticket filter narrowed the response. */
export interface CostFiltered {
  ticketFilterApplied?: boolean;
}

/** `GET /cost/ticket-types`: the channels and intent categories of the range's tickets, with counts. */
export interface CostTicketTypes {
  channels: { key: string; label: string; tickets: number }[];
  categories: { key: string; tickets: number; intents: { key: string; tickets: number }[] }[];
  unclassifiedTickets: number;
}

export interface CostHealth {
  /** Calls in this account and range with no price yet. */
  unpricedEvents: number;
  /** Company-wide: cost of rows that know no account. */
  unattributedCostUsd: number;
  /** Company-wide: ledger / provider-reported spend (0..1), null until reconciliation has data. */
  providerSpendTrackedShare: number | null;
  /** Company-wide: last reconciled day (YYYY-MM-DD). */
  reconciledThrough: string | null;
  /** Company-wide: ISO time of the last rollup. */
  lastRollupAt: string | null;
}

export interface CostSummary extends CostFiltered {
  totalCostUsd: number;
  llmCalls: number;
  apiCalls: number;
  tickets: number;
  responses: number;
  /** Null when there are no tickets / responses (divisor 0). */
  directCostPerTicket: number | null;
  fullyLoadedCostPerTicket: number | null;
  costPerResponse: number | null;
  /** Responses / tickets; null when there are no tickets. */
  avgResponsesPerTicket: number | null;
  /** Total cost minus the cost of the tickets that started in the range. */
  overheadCostUsd: number;
  health: CostHealth;
}

export interface CostSeriesPoint {
  period: string; // YYYY-MM-DD | YYYY-MM | YYYY
  /** Null for groupBy=none, a missing model, or brand-less (account-level) cost. */
  key: string | null;
  /** groupBy=area only: the area's display name. */
  label?: string;
  costUsd: number;
  events: number;
}

export interface CostSeries extends CostFiltered {
  granularity: CostGranularity;
  groupBy: CostSeriesGroupBy;
  points: CostSeriesPoint[];
}

export interface CostBreakdownRow {
  key: string | null;
  /** groupBy=area only: the area's display name. */
  label?: string;
  costUsd: number;
  events: number;
  inputTokens: number;
  outputTokens: number;
  /** 0..1 share of the range's cost. */
  share: number;
}

export interface CostBreakdown extends CostFiltered {
  groupBy: CostBreakdownGroupBy;
  rows: CostBreakdownRow[];
}

export interface CostTicketRow {
  conversationId: number;
  buId: number | null;
  firstAt: string;
  lastAt: string;
  responses: number;
  llmCalls: number;
  apiCalls: number;
  costUsd: number;
}

export interface CostTickets extends CostFiltered {
  total: number;
  rows: CostTicketRow[];
}

export interface CostTicketEvent {
  occurredAt: string;
  service: string;
  feature: string;
  kind: string;
  provider: string;
  model: string | null;
  inputTokens: number;
  cachedInputTokens: number;
  outputTokens: number;
  /** Null while unpriced. */
  costUsd: number | null;
  traceId: string | null;
}

export interface CostTicketResponse {
  /** Null groups the calls made for no particular response ("ticket-level work"). */
  messageId: string | null;
  requestId: string | null;
  costUsd: number;
  events: CostTicketEvent[];
}

export interface CostTicketDetail {
  conversationId: number;
  costUsd: number;
  responses: CostTicketResponse[];
}

export type CostCategoryKey =
  | 'response_generation'
  | 'tickets_to_sop'
  | 'insights'
  | 'intent_classification'
  | 'knowledge_base'
  | 'testing'
  | 'gap_report'
  | 'helpdesk_api'
  | 'other';

/** What one unit of a feature area's work is; null when the area has no natural unit. */
export type CostUnit = 'response' | 'run' | 'insight' | 'ticket';

export interface CostFeatureCategory {
  key: CostCategoryKey;
  label: string;
  costUsd: number;
  /** 0..1 share of the range's cost. */
  share: number;
  events: number;
  unit: CostUnit | null;
  units: number;
  /** Null when `units` is 0 or the area has no unit. */
  avgCostPerUnit: number | null;
  /** The raw ledger features mapped to this area. */
  features: { key: string; costUsd: number; events: number }[];
}

/** `GET /cost/features`: spend by feature area. */
export interface CostFeatures extends CostFiltered {
  categories: CostFeatureCategory[];
  /** Ledger features with no area mapping yet; their cost is under `other`. */
  unmappedFeatures: string[];
}

/** `GET /cost/weekly?level=`: bucket by ticket (start week) or by response. */
export type CostWeeklyLevel = 'ticket' | 'response';

/** ISO-8601 weeks (Monday–Sunday); `year` is the ISO year, so late Dec / early Jan can differ. */
export interface CostWeek {
  year: number;
  week: number;
  weekStart: string; // YYYY-MM-DD
  weekEnd: string; // YYYY-MM-DD, inclusive
}

export interface CostWeeklyRow extends CostWeek {
  /** Null for account-level (brand-less) cost. */
  buId: number | null;
  brandName: string;
  tickets: number;
  responses: number;
  responsesPerTicket: number | null;
  ticketCostUsd: number;
  avgTicketCostUsd: number | null;
  avgResponseCostUsd: number | null;
  totalCostUsd: number;
  categories: Partial<Record<CostCategoryKey, number>>;
}

export interface CostWeekly extends CostFiltered {
  level: CostWeeklyLevel;
  weeks: CostWeek[];
  rows: CostWeeklyRow[];
}

/** One email on the AI cost allowlist (kim-ai-backend ai_cost_viewers). */
export interface CostViewer {
  email: string;
  addedBy: string | null;
  createdAt: string;
}

export interface CostViewerList {
  viewers: CostViewer[];
  canView: true;
}

/** One usage_prices row in force now or scheduled (kim-ai-backend /api/analytics/cost-prices). */
export interface UsagePrice {
  id: number;
  provider: string;
  model: string;
  inputPerMtok: number;
  cachedInputPerMtok: number | null;
  cacheWritePerMtok: number | null;
  outputPerMtok: number;
  perUnit: number;
  effectiveFrom: string;
  effectiveTo: string | null;
  /** `manual…` rows are never touched by the daily Langfuse sync. */
  source: string;
  repricePending: boolean;
}

/** A model called in the last 30 days with no price row: its calls count as $0. */
export interface UnpricedModel {
  provider: string;
  model: string;
  events: number;
  lastSeen: string;
}

export interface UsagePriceList {
  prices: UsagePrice[];
  unpriced: UnpricedModel[];
}

export interface UsagePriceInput {
  provider: string;
  model: string;
  inputPerMtok: number;
  cachedInputPerMtok: number | null;
  cacheWritePerMtok: number | null;
  outputPerMtok: number;
  perUnit: number;
  /** YYYY-MM-DD, UTC. */
  effectiveFrom: string;
}

export interface CostAccess {
  canView: boolean;
}
